import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const PRICE_TO_PLAN: Record<string, "essentiel" | "pro"> = {
  [import.meta.env.VITE_STRIPE_PRICE_GRATUIT]: "essentiel",
  [import.meta.env.VITE_STRIPE_PRICE_PRO]: "pro",
  [import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL]: "pro",
};

type SubRow = {
  /** Source de vérité commerciale : 'pro' | 'découverte'. Synchronisé par webhook Stripe uniquement. */
  plan: string | null;
  stripe_subscription_status: string | null;
  stripe_price_id: string | null;
  trial_started_at: string | null;
  reminded_at: string | null;
  created_at: string | null;
  trial_used: boolean | null;
  ia_trial_used: number | null;
};

export type FeatureFlags = {
  assistant_ia: boolean;
  simulateur: boolean;
  projection: boolean;
  export_pdf: boolean;
  taux_horaire: boolean;
  rappels_urssaf: boolean;
  export_2035: boolean;
  multi_annees: boolean;
};

export type SubscriptionData = {
  isActive: boolean;
  isTrial: boolean;
  trialUsed: boolean;
  iaTrialUsed: number;
  daysLeftInTrial: number;
  hoursLeftInTrial: number;
  minutesLeftInTrial: number;
  trialEndAt: Date | null;
  plan: "essentiel" | "pro" | null;
  status: string;
  remindedAt: string | null;
  loading: boolean;
  refetch: () => void;
  features: FeatureFlags;
};

export function useSubscription(): SubscriptionData {
  const { user, loading: authLoading } = useAuth();
  const [row, setRow] = useState<SubRow | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSub = useCallback(async () => {
    if (!user) {
      setRow(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("profils")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        if (import.meta.env.DEV) {
          console.warn("[useSubscription] fallback free after fetch error:", error.message);
        }
        setRow(null);
        return;
      }

      setRow(data as unknown as SubRow | null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[useSubscription] unexpected fetch error, fallback to free:", error);
      }
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    fetchSub();
    if (!user) return;

    const channel = supabase
      .channel(`profils-sub-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profils",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            setRow(null);
            return;
          }
          setRow(payload.new as SubRow);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, authLoading, fetchSub]);

  // ── Source de vérité unique : stripe_subscription_status ──
  const rawStatus = typeof row?.stripe_subscription_status === "string"
    ? row.stripe_subscription_status.toLowerCase()
    : null;
  const priceId = row?.stripe_price_id ?? null;
  const trialUsed = row?.trial_used ?? false;

  // isTrial = true ONLY when Stripe explicitly says "trialing".
  // No date calculation, no created_at inference.
  const isTrial = rawStatus === "trialing";

  // Countdown only for users actively trialing.
  // Fallback to created_at if trial_started_at is null (webhook delay / missing data).
  const trialStartSource = isTrial
    ? (row?.trial_started_at ?? row?.created_at ?? null)
    : null;
  const trialStartedAt = trialStartSource ? new Date(trialStartSource) : null;
  const trialEndAt = trialStartedAt
    ? new Date(trialStartedAt.getTime() + 14 * 24 * 60 * 60 * 1000)
    : null;

  const now = new Date();
  const msLeft = trialEndAt ? Math.max(0, trialEndAt.getTime() - now.getTime()) : 0;

  // For free users (no trial), all countdown values are 0
  const daysLeftInTrial = isTrial ? Math.floor(msLeft / (1000 * 60 * 60 * 24)) : 0;
  const hoursLeftInTrial = isTrial ? Math.floor((msLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)) : 0;
  const minutesLeftInTrial = isTrial ? Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60)) : 0;

  const isActive = rawStatus === "active" || isTrial;
  // Fallback: null/undefined/empty → "free"
  const status = rawStatus ?? "free";

  const devOverride = (import.meta.env.DEV && typeof window !== "undefined")
    ? (window as any).__DEV_PLAN_OVERRIDE__ as "essentiel" | "pro" | null | undefined
    : undefined;

  // Plan : colonne DB comme source de vérité (webhook Stripe la synchronise).
  // Fallback sur l'inférence price_id pour les profils antérieurs à la migration
  // 20260323120000_add_plan_column_stripe_security.sql (dbPlan = null).
  const dbPlan = row?.plan ?? null;
  const inferredPaidPlan: "essentiel" | "pro" = priceId
    ? (PRICE_TO_PLAN[priceId] ?? "pro")
    : "pro";

  // resolvedActivePlan : DB plan en priorité, price_id en fallback pour la rétrocompat.
  const resolvedActivePlan: "essentiel" | "pro" =
    dbPlan === "pro" ? "pro"
    : dbPlan === "essentiel" ? "essentiel"
    : inferredPaidPlan;

  // plan est non-null uniquement si :
  //   1. L'abonnement Stripe est actif (isActive)
  //   2. ET la DB ne dit pas explicitement 'découverte' (double garde-fou)
  // Cela couvre à la fois les cas de latence webhook et les états incohérents.
  const plan: "essentiel" | "pro" | null = devOverride
    ? devOverride
    : isActive && dbPlan !== "découverte"
      ? resolvedActivePlan
      : null;

  const features = useMemo<FeatureFlags>(() => {
    // isActive && plan : double condition — pas de fallback "pro" implicite.
    // Un utilisateur actif sans plan résolu (null) doit recevoir "none" et non "pro".
    const effectivePlan = (isActive && plan) ? plan : "none";
    return {
      assistant_ia: effectivePlan === "pro",
      simulateur: ["essentiel", "pro"].includes(effectivePlan),
      projection: effectivePlan === "pro",
      export_pdf: effectivePlan === "pro",
      taux_horaire: effectivePlan === "pro",
      rappels_urssaf: effectivePlan === "pro",
      export_2035: effectivePlan === "pro",
      multi_annees: effectivePlan === "pro",
    };
  }, [plan, isActive]);

  return {
    isActive,
    isTrial,
    trialUsed,
    iaTrialUsed: row?.ia_trial_used ?? 0,
    daysLeftInTrial,
    hoursLeftInTrial,
    minutesLeftInTrial,
    trialEndAt,
    plan,
    status,
    remindedAt: row?.reminded_at ?? null,
    loading,
    refetch: fetchSub,
    features,
  };
}
